import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { ProfileInfoCard } from "@/components/profile-info-card";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@/components/ui/item";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/utils/trpc";

type BadgeVariant = "default" | "warning" | "destructive" | "outline";

const statusConfig = {
  approved: { label: "Approved", variant: "default" as BadgeVariant },
  pending: { label: "Pending", variant: "warning" as BadgeVariant },
  rejected: { label: "Rejected", variant: "destructive" as BadgeVariant },
  removed: { label: "Removed", variant: "outline" as BadgeVariant },
} as const;

type ApprovalStatus = keyof typeof statusConfig;

const statusOrder: ApprovalStatus[] = [
  "approved",
  "pending",
  "rejected",
  "removed",
];

type WorkerApprovalRecord = {
  id: string;
  siteId: string;
  siteName: string;
  status: string;
  reason: string | null;
};

function WorkerApprovalList({
  approvals,
  onStatusChange,
  pendingId,
}: {
  approvals: WorkerApprovalRecord[];
  onStatusChange: (approvalId: string, status: ApprovalStatus) => void;
  pendingId: string | null;
}) {
  if (approvals.length === 0) {
    return <div className="text-muted-foreground">No approvals found.</div>;
  }

  return (
    <div className="space-y-2">
      {approvals.map((approval) => {
        const reason = approval.reason?.trim();
        const isUpdating = pendingId === approval.id;

        return (
          <Item key={approval.id} variant="outline">
            <ItemContent>
              <ItemTitle>{approval.siteName}</ItemTitle>
            </ItemContent>
            <ItemActions>
              <Select
                value={approval.status}
                onValueChange={(value) => {
                  if (value !== approval.status && value in statusConfig) {
                    onStatusChange(approval.id, value as ApprovalStatus);
                  }
                }}
                disabled={isUpdating}
              >
                <SelectTrigger size="sm" aria-label="Change approval status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOrder.map((status) => {
                    const option = statusConfig[status];
                    return (
                      <SelectItem key={status} value={status}>
                        {option.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {reason && <ItemDescription>Note: {reason}</ItemDescription>}
            </ItemActions>
          </Item>
        );
      })}
    </div>
  );
}

export default function WorkerApprovals() {
  const { id } = useParams();
  const workerId = id || "";
  const queryClient = useQueryClient();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const profile = useQuery(
    trpc.getWorkerProfile.queryOptions({ id: workerId })
  );
  const approvals = useQuery(
    trpc.getWorkerApprovals.queryOptions({ workerId })
  );

  const updateApproval = useMutation({
    ...trpc.updateWorkerApprovalStatus.mutationOptions(),
    onSuccess: () => {
      toast.success("Approval status updated");
      queryClient.invalidateQueries({
        queryKey: trpc.getWorkerApprovals.queryKey({ workerId }),
      });
    },
    onError: (error) => {
      const message =
        (error as { message?: string })?.message ??
        "Failed to update approval status";
      toast.error(message);
    },
  });

  const records: WorkerApprovalRecord[] = (approvals.data ?? []).map(
    (approval) => ({
      id: approval.id,
      siteId: approval.siteId,
      siteName: approval.siteName,
      status: approval.status,
      reason: approval.reason,
    })
  );

  const handleStatusChange = (approvalId: string, status: ApprovalStatus) => {
    setPendingId(approvalId);
    updateApproval.mutate(
      { approvalId, status },
      {
        onSettled: () => {
          setPendingId(null);
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={profile.data?.fullName ?? "Worker Approvals"}
        description="Site approval history"
        backTo={`/workers/${workerId}`}
      />
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 lg:gap-8 items-start">
        <div className="lg:sticky lg:top-24">
          {profile.data && <ProfileInfoCard profile={profile.data} />}
        </div>
        <div className="space-y-2">
          {approvals.isLoading && (
            <div className="text-muted-foreground">Loading approvals…</div>
          )}
          {approvals.error && (
            <div className="text-red-600">{approvals.error.message}</div>
          )}
          {!approvals.isLoading && !approvals.error && (
            <WorkerApprovalList
              approvals={records}
              onStatusChange={handleStatusChange}
              pendingId={pendingId}
            />
          )}
        </div>
      </div>
    </div>
  );
}
